import { useMemo, useCallback, useEffect } from 'react'

import type { BillingInterval, ObPlanId } from '../../../gql/graphql'

import { BillingIntervalValues, OBPlanIdValues } from '../types'
import type { PlanInfo, PlanCalculation } from '../types'
import { CHANNEL_LIMITS } from '../constants'
import { isMobileBillingGateway } from '../../../common/utils/billing'
import { useDiscountCalculations } from './useDiscountCalculations'
import { usePlanSelection } from './usePlanSelection'
import { usePlanSelectorQuery } from './usePlanSelectorQuery'
import { usePricingCalculations } from './usePricingCalculations'
import {
  findCurrentPlan,
  filterPlansByBillingCycle,
  createPlanInfo,
  createCurrentPlanInfo,
} from '../utils/dataTransformers'
import type { ExtendedPlanSelectorData } from '../utils/dataTransformers'
import * as planComparison from '../utils/planComparison'
import type { PlanComparisonConfig } from '../utils/planComparison'

interface UsePlanSelectorReturn {
  data: ExtendedPlanSelectorData | null
  loading: boolean
  error: Error | null
  selection: PlanInfo
  currentUser: {
    currentPlan: PlanInfo
    trial: { startDate: string; endDate: string } | null
    isChurning: boolean
    hasEverBeenPayingCustomer: boolean
    hasPaymentMethod: boolean
    currentlyPayingRaw: number
  }
  selectChannels: (count: number) => void
  selectBillingCycle: (cycle: BillingInterval) => void
  isPlanCurrent: (planId: ObPlanId) => boolean
  isPlanUpgrade: (planId: ObPlanId) => boolean
  isPlanDowngrade: (planId: ObPlanId) => boolean
  isPlanUpdate: (planId: ObPlanId) => boolean
  isPayingViaMobile: boolean
  refetch: () => Promise<unknown>
}

/**
 * Custom hook that orchestrates the PlanSelector component functionality.
 *
 * Integrates multiple specialized hooks for different concerns:
 * - usePricingCalculations: Handles tiered pricing and savings calculations
 * - useDiscountCalculations: Manages discount application logic
 * - planComparison utilities: Pure functions for plan hierarchy and comparison
 * - usePlanSelection: Manages selection state
 * - usePlanSelectorQuery: Fetches plan data from GraphQL
 *
 * @param props - Configuration object containing the plan selection callback
 * @returns Complete plan selector interface with all necessary functions and data
 */
export function usePlanSelector(): UsePlanSelectorReturn {
  const { data, loading, error, refetch } = usePlanSelectorQuery()
  const currentlyPayingRaw = data?.nextInvoice?.totalAmount ?? 0

  const isPayingViaMobile = isMobileBillingGateway(data?.gateway)

  const {
    selectedChannelCount,
    selectedBillingCycle,
    selectChannels,
    selectBillingCycle,
  } = usePlanSelection({
    initialChannelCount: Math.max(
      CHANNEL_LIMITS.min,
      data?.subscription?.quantity ?? CHANNEL_LIMITS.default,
    ),
    initialBillingCycle: BillingIntervalValues.year,
  })

  // Ensure selectedChannelCount is always a valid number
  const safeSelectedChannelCount =
    typeof selectedChannelCount === 'number' && !isNaN(selectedChannelCount)
      ? Math.max(CHANNEL_LIMITS.min, selectedChannelCount)
      : CHANNEL_LIMITS.default

  useEffect(() => {
    if (data?.subscription?.quantity) {
      selectChannels(data.subscription.quantity)
    }
  }, [data?.subscription?.quantity, selectChannels])

  const activeSubscriptionBillingCycle =
    data?.subscription?.plan.id === OBPlanIdValues.free
      ? null
      : data?.subscription?.interval

  useEffect(() => {
    if (activeSubscriptionBillingCycle) {
      selectBillingCycle(activeSubscriptionBillingCycle)
    }
  }, [activeSubscriptionBillingCycle, selectBillingCycle])

  const currentPlanOption = useMemo(
    () =>
      data
        ? findCurrentPlan(
            data.availablePlans,
            data.subscription.plan.id,
            data.subscription.interval || selectedBillingCycle,
          )
        : null,
    [data, selectedBillingCycle],
  )

  const planOptions = useMemo(
    () =>
      data
        ? filterPlansByBillingCycle(data.availablePlans, selectedBillingCycle)
        : [],
    [data, selectedBillingCycle],
  )

  const pricingCalculations = usePricingCalculations({
    planOptions: data?.availablePlans ?? [],
    channelCount: safeSelectedChannelCount,
    billingCycle: selectedBillingCycle,
  })

  const discountCalculations = useDiscountCalculations({
    discounts: data?.discounts || [],
  })

  const planComparisonConfig: PlanComparisonConfig = useMemo(
    () => ({
      currentPlanId: currentPlanOption?.planId || OBPlanIdValues.free,
      currentChannelCount: Math.max(
        CHANNEL_LIMITS.min,
        data?.subscription?.quantity ?? CHANNEL_LIMITS.default,
      ),
      selectedChannelCount: safeSelectedChannelCount,
      currentBillingCycle: data?.subscription?.interval,
      selectedBillingCycle,
    }),
    [
      currentPlanOption?.planId,
      data?.subscription?.quantity,
      safeSelectedChannelCount,
      data?.subscription?.interval,
      selectedBillingCycle,
    ],
  )

  const finalCalculations = useMemo(() => {
    const basePricing = pricingCalculations.getAllCalculations()
    const result: Record<string, PlanCalculation> = {}

    Object.entries(basePricing).forEach(([planId, calculation]) => {
      const discountedCalculation =
        discountCalculations.applyDiscounts(calculation)
      result[planId] = discountedCalculation
    })

    return result
  }, [pricingCalculations, discountCalculations])

  const extendedData = useMemo((): ExtendedPlanSelectorData | null => {
    const extendedPlanOptions = planOptions.map((plan) => ({
      ...plan,
      calculations: finalCalculations[plan.planId] || {
        pricePerChannel: 0,
        totalPrice: 0,
        basePrice: 0,
      },
      appliedDiscounts: data?.discounts || [],
    }))

    return data
      ? {
          ...data,
          availablePlans: extendedPlanOptions,
        }
      : null
  }, [data, planOptions, finalCalculations])

  const isPlanCurrent = useCallback(
    (planId: ObPlanId) =>
      planComparison.isPlanCurrent(planId, planComparisonConfig),
    [planComparisonConfig],
  )

  const isPlanUpgrade = useCallback(
    (planId: ObPlanId) =>
      planComparison.isPlanUpgrade(planId, planComparisonConfig),
    [planComparisonConfig],
  )

  const isPlanDowngrade = useCallback(
    (planId: ObPlanId) =>
      planComparison.isPlanDowngrade(planId, planComparisonConfig),
    [planComparisonConfig],
  )

  const isPlanUpdate = useCallback(
    (planId: ObPlanId) =>
      planComparison.isPlanUpdate(planId, planComparisonConfig),
    [planComparisonConfig],
  )

  const selection = useMemo(
    () =>
      currentPlanOption
        ? createPlanInfo(
            currentPlanOption,
            safeSelectedChannelCount,
            selectedBillingCycle,
            finalCalculations,
          )
        : {
            planId: OBPlanIdValues.free,
            planName: 'Free',
            channelCount: safeSelectedChannelCount,
            billingCyclePrice: 0,
            billingCycle: selectedBillingCycle,
            pricePerChannel: 0,
          },
    [
      currentPlanOption,
      safeSelectedChannelCount,
      selectedBillingCycle,
      finalCalculations,
    ],
  )

  const currentPlanInfo = useMemo(
    () =>
      currentPlanOption
        ? createCurrentPlanInfo(
            currentPlanOption,
            Math.max(
              CHANNEL_LIMITS.min,
              data?.subscription?.quantity ?? CHANNEL_LIMITS.default,
            ),
          )
        : {
            planId: OBPlanIdValues.free,
            planName: 'Free',
            channelCount: CHANNEL_LIMITS.default,
            billingCyclePrice: 0,
            pricePerChannel: 0,
            billingCycle: BillingIntervalValues.year,
          },
    [currentPlanOption, data?.subscription?.quantity],
  )

  const currentUser = useMemo(
    () => ({
      currentPlan: currentPlanInfo,
      isChurning: data?.subscription?.isCanceledAtPeriodEnd || false,
      hasEverBeenPayingCustomer: data?.hasEverBeenPayingCustomer || false,
      hasPaymentMethod: data?.hasDefaultStripePaymentMethod || false,
      trial: data?.subscription?.trial || null,
      currentlyPayingRaw,
    }),
    [
      currentPlanInfo,
      data?.subscription?.isCanceledAtPeriodEnd,
      data?.hasEverBeenPayingCustomer,
      data?.hasDefaultStripePaymentMethod,
      data?.subscription?.trial,
      currentlyPayingRaw,
    ],
  )

  return {
    data: extendedData,
    loading,
    error,
    selectChannels,
    selectBillingCycle,
    isPlanCurrent,
    isPlanUpgrade,
    isPlanDowngrade,
    isPlanUpdate,
    selection,
    currentUser,
    isPayingViaMobile,
    refetch,
  }
}
